import Fuse from 'fuse.js';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useSessionStorage } from 'react-use';
import styled from 'styled-components';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../../concepts/objects';
import { Ticker } from '../../../concepts/objects/ObjectSymbols';
import { Colors, Input } from '../../../ui';
import { useResolve } from '../../Container';
import { Flexer, Spacer } from '../../dashboard/elements/LayoutElements';
import { cameraFrameSelection } from '../commands/CameraCommands';
import { select } from '../commands/SelectionCommands';
import { Physical } from '../entities/Physical';

const Branch = styled.div`
    position: absolute;
    height: 100%;
    border-left: solid 1px ${Colors.border};
    border-bottom: solid 1px ${Colors.border};
    width: 1.5rem;
    left: -1.25rem;
    top: -50%;
`;

const ComponentLink = styled.div`
    display: inline-block;
    &:hover {
        cursor: pointer;
        color: ${Colors.primary};
        text-decoration: underline;
    }
`;

const ComponentItem = ({ object, children }: { object: Component, children?: ReactNode }) => {

    const dispatch = useResolve(CommandDispatcher);

    const onClick = () => {
        dispatch(select, [[object.id]]);
        dispatch(cameraFrameSelection);
    };

    return <Flexer relative>
        <Flexer padding="0 0 0 1.5">
            <ComponentLink onClick={onClick}>{object.toHumanString()}</ComponentLink>
            {children}
        </Flexer>
    </Flexer>;
};

const ComponentInfo = ({ object }: { object: Component }) => {

    const [ready, setReady] = useState(false);
    const [ticker, setTicker] = useState(-1);

    const objects = useResolve(ObjectManager);
    useEffect(() => {
        objects.on(object.id, 'affected', ([x]) => setTicker(x[Ticker]));
        setReady(true);
    }, []);

    if (!object.alive) return null;
    if (!ready) return null;

    return <ComponentItem key={object.id} object={object}>
        {object.polarize(Physical).map((x: any) => <ComponentInfo key={x.id} object={x} />)}
    </ComponentItem>;
};

export const DeveloperModelInfo = () => {

    const objects = useResolve(ObjectManager);
    const [filter, setFilter] = useSessionStorage('deqo/ui/developer/model/filter', '');
    const from = useCallback((state: Record<string, any>) => {
        const keys = Object.keys(state);
        const filtered = !!filter
            ? new Fuse(keys, { includeScore: true }).search(filter).filter(x => x.score < 0.1).map(x => x.item)
            : keys;
        return filtered.sort();
    }, [filter]);

    return <>
        <Input placeholder="Filter" value={filter} onChange={setFilter} autoFocus />
        <Spacer f={2} />
        <ComponentInfo object={objects.getRoot()} />
    </>;
};
